// mergeBranchOps — VQE-Edit edit-action wrappers for the MERGE branch
// STRUCTURAL ops (§2.5 S7): add-merge-branch / remove-merge-branch /
// modify-merge-branch-action. Distinct from `modifyMergeBranch.ts`, which
// wraps the branch-CONTENT ops (condition text + nested SET assignments).
//
// Per the binding orchestrator wrapper contract (Plan 12 landed): these
// wrappers keep the validate-and-THROW-before-commitEdit idiom
// (`modifyMergeBranch.ts`'s validation shape) but return `Promise<CommitOutcome>`
// instead of `Promise<void>` — mirror `addFilter.ts` / `insertConflictClause.ts`,
// NOT `modifyMergeBranch.ts` (a pre-Plan-12 relic that still discards the
// outcome).
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — MERGE branch ops (§2.5 S7).

import type { ParseOptions } from '../../../../sql-grammar/model';
import type { MergeBranch, MergeBranchKind, QueryStructure } from '../../../../sql-resolver/types';
import type { WhenBranch } from '../../render/merge/parseMergeBranch';
import { vqeCopy } from '../../ux/vqeCopy';
import { commitEdit, type CommitEditCtx, type CommitOutcome } from '../commitEdit';
import type { VqeEditContext } from '../editContext';
import type { MergeBranchSelector } from './modifyMergeBranch';

export type MergeBranchStructuralOptions = {
  /** Optional structure used for branch-count / out-of-range validation. */
  readonly structure?: QueryStructure;
};

function branchList(kind: MergeBranchKind, structure: QueryStructure): readonly MergeBranch[] {
  return (kind === 'matched' ? structure.mergeWhenMatched : structure.mergeWhenNotMatched) ?? [];
}

function totalBranchCount(structure: QueryStructure): number {
  return (structure.mergeWhenMatched?.length ?? 0) + (structure.mergeWhenNotMatched?.length ?? 0);
}

/** Appends a pre-synthesized `WHEN … THEN …` branch (decisions 3–5 build `branchText`). */
export async function addMergeBranch(
  branchText: string,
  ctx: CommitEditCtx,
): Promise<CommitOutcome> {
  const text = branchText.trim();
  if (text.length === 0) {
    throw new Error('addMergeBranch: branchText must not be empty');
  }
  return commitEdit({ op: 'add-merge-branch', branchText: text }, ctx);
}

/**
 * Removes the selected WHEN branch. Decision 6: removing the LAST remaining
 * branch is refused — a WHEN-less MERGE is invalid on every dialect that has
 * MERGE — so this throws BEFORE `commitEdit` when `options.structure` shows
 * only one branch total (the resolver op also refuses, defense in depth).
 */
export async function removeMergeBranch(
  selector: MergeBranchSelector,
  ctx: CommitEditCtx,
  options: MergeBranchStructuralOptions = {},
): Promise<CommitOutcome> {
  if (!Number.isInteger(selector.branchIndex) || selector.branchIndex < 0) {
    throw new Error(
      `removeMergeBranch: branchIndex must be a non-negative integer (got ${selector.branchIndex})`,
    );
  }
  if (options.structure) {
    const list = branchList(selector.branchKind, options.structure);
    if (selector.branchIndex >= list.length) {
      throw new Error(
        `removeMergeBranch: branch ${selector.branchKind}#${selector.branchIndex} is out of range`,
      );
    }
    if (totalBranchCount(options.structure) <= 1) {
      throw new Error('removeMergeBranch: cannot remove the last remaining WHEN branch');
    }
  }
  return commitEdit(
    { op: 'remove-merge-branch', branchKind: selector.branchKind, branchIndex: selector.branchIndex },
    ctx,
  );
}

/**
 * Switches a WHEN branch's action body (decision 4: matched-side UPDATE ⇄
 * DELETE only). `newActionText` is the full THEN-body replacement — e.g.
 * `'DELETE'` or `'UPDATE SET a = s.a'` — built by the caller
 * (`MergeActionKindDropdown` synthesizes the UPDATE body via
 * `mergeBranchTemplates`).
 */
export async function modifyMergeBranchAction(
  selector: MergeBranchSelector,
  newActionText: string,
  ctx: CommitEditCtx,
  options: MergeBranchStructuralOptions = {},
): Promise<CommitOutcome> {
  const v = newActionText.trim();
  if (v.length === 0) {
    throw new Error('modifyMergeBranchAction: newActionText must not be empty');
  }
  if (options.structure) {
    const list = branchList(selector.branchKind, options.structure);
    if (selector.branchIndex < 0 || selector.branchIndex >= list.length) {
      throw new Error(
        `modifyMergeBranchAction: branch ${selector.branchKind}#${selector.branchIndex} is out of range`,
      );
    }
  }
  return commitEdit(
    {
      op: 'modify-merge-branch-action',
      branchKind: selector.branchKind,
      branchIndex: selector.branchIndex,
      newActionText: v,
    },
    ctx,
  );
}

export type RemoveBranchGate =
  | { readonly disabled: true; readonly tooltip: string }
  | { readonly disabled: false };

/**
 * UI gate for the per-branch remove button (§2.5 decision 6). The structural
 * "last branch" reason is checked BEFORE readiness (mirrors
 * `MergeActionKindDropdown`'s priority): it stays true even once ready, so it
 * shouldn't be described as a readiness problem. Shares no state with
 * `removeMergeBranch`'s own defense-in-depth check — this is the UI-facing
 * mirror of the same rule.
 */
export function computeRemoveBranchGate(
  editContext: VqeEditContext | undefined,
  selector: MergeBranchSelector | undefined,
  totalBranchCount: number,
): RemoveBranchGate {
  if (!editContext || !selector) {
    return { disabled: true, tooltip: vqeCopy.disabled.editingUnavailable };
  }
  if (totalBranchCount <= 1) {
    return { disabled: true, tooltip: vqeCopy.disabled.mergeRemoveLastBranch };
  }
  if (!editContext.service.getSchemaSnapshot()) {
    return { disabled: true, tooltip: vqeCopy.disabled.mergeRemoveNotReady };
  }
  return { disabled: false };
}

export type ActionKindGate =
  | { readonly disabled: true; readonly tooltip: string }
  | { readonly disabled: false };

/**
 * UI gate for `MergeActionKindDropdown` (§2.5 decisions 3+4) — kept here
 * (not in the `.tsx` file) so the component module only exports components
 * (react-refresh/only-export-components), mirroring `computeRemoveBranchGate`
 * above. Structural reasons (Oracle's blanket veto, a plain NOT MATCHED
 * branch's INSERT-only shape) are checked BEFORE readiness so the tooltip
 * never claims "connect and wait for the schema" for a switch that stays
 * unavailable once ready anyway.
 */
export function computeActionKindGate(
  branch: WhenBranch,
  selector: MergeBranchSelector | undefined,
  editContext: VqeEditContext | undefined,
  engine: ParseOptions['engine'],
): ActionKindGate {
  if (!editContext || !selector) {
    return { disabled: true, tooltip: vqeCopy.disabled.editingUnavailable };
  }
  if (engine === 'oracle') {
    return { disabled: true, tooltip: vqeCopy.disabled.mergeActionOracle };
  }
  if (branch.matchKind === 'not-matched') {
    return { disabled: true, tooltip: vqeCopy.disabled.mergeActionInsertOnly };
  }
  if (!editContext.service.getSchemaSnapshot()) {
    return { disabled: true, tooltip: vqeCopy.disabled.mergeActionNotReady };
  }
  return { disabled: false };
}

export type AddWhenBranchKind = 'matched-update' | 'matched-delete' | 'not-matched-insert';

/**
 * Decision 3: which add-branch templates the menu offers for a given engine +
 * doc-order shape. Kept here (not in `AddWhenBranchMenu.tsx`) for the same
 * component-purity reason as `computeActionKindGate` above.
 */
export function computeAddWhenBranchItems(
  engine: ParseOptions['engine'],
  matchedCount: number,
  notMatchedCount: number,
): readonly AddWhenBranchKind[] {
  if (engine !== 'oracle') {
    return ['matched-update', 'matched-delete', 'not-matched-insert'];
  }
  const items: AddWhenBranchKind[] = [];
  if (matchedCount === 0) items.push('matched-update');
  if (notMatchedCount === 0) items.push('not-matched-insert');
  return items;
}
